import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';

/**
 * user index
 */
import {
  IUserInfoDto,
  IUserPayload,
  IUserInfoService,
  IUserAuthzService,
  IUserPayloadService,
  IUserRoleControlService,
  IUserInfoControlService,
  IUserInfoModelService
} from './interfaces';


/**
 * utils
 */
import { PermissionName } from './utilities';
import { JwtUtils } from '@core/utilities';

/**
 * models
 */
import { UserInfoModel } from './models';

/**
 * mappers
 */
import { UserDtoMapper } from './utilities';

/**
 * identity
 */
import { AUTH_ACCESS_TOKEN, IAuthAccessTokenService } from '@identity/authentication';
import { ContentPageModules, AuthorizationUtils, IModulePermission } from '@identity/utilities';

/**
 * Comprehensive user management service for identity and authorization
 * @class UserService
 * @description Provides complete user management including personal information, authorization data, 
 * permissions, and access control. Integrates with JWT token management and authorization utilities 
 * to provide a unified user experience.
 * 
 * @implements {IUserPayloadService} User payload data management
 * @implements {IUserInfoService} User information management
 * @implements {IUserAuthzService} User authorization management
 * @implements {IUserRoleControlService} User role control
 * @implements {IUserInfoControlService} User information control
 * @implements {IUserAuthzControlService} User authorization control
 * 
 * @example
 * ```typescript
 * // Inject the service using dependency injection tokens
 * constructor(@Inject(USER_INFO) private userInfo: IUserInfoService) {}
 * constructor(@Inject(USER_AUTHZ) private userAuthz: IUserAuthzService) {}
 * constructor(@Inject(USER_AUTHZ_CONTROL) private authzControl: IUserAuthzControlService) {}
 * 
 * // Check user information
 * if (this.userInfo.userInfo) {
 *   console.log('User name:', this.userInfo.userInfo.firstName, this.userInfo.userInfo.lastName);
 * }
 * 
 * // Check permissions
 * if (this.authzControl.hasViewerAccessToModule(ContentPageModules.DASHBOARD)) {
 *   // User can view dashboard
 * }
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
@Injectable()
export class UserService
  implements IUserPayloadService, IUserInfoService, IUserAuthzService, IUserRoleControlService, IUserInfoControlService, IUserInfoModelService {

  /**
   * Observable stream of user payload data
   * @public
   * @readonly
   * @type {Observable<IUserPayload | null>}
   * @description Emits the current user payload data whenever it changes.
   * Derived from JWT token decoding and provides complete user authorization information.
   * 
   * @example
   * ```typescript
   * this.userService.payload$.subscribe(payload => {
   *   if (payload) {
   *     console.log('User role:', payload.role_name);
   *     console.log('Facility ID:', payload.facility_id);
   *     console.log('Authorized units:', payload.authorized_units);
   *   }
   * });
   * ```
   */
  public readonly payload$!: Observable<IUserPayload | null>

  /**
   * Current user payload data
   * @public
   * @readonly
   * @type {IUserPayload | null}
   * @description Returns the current user payload or null if not available.
   * Provides synchronous access to user authorization data from JWT token.
   * 
   * @example
   * ```typescript
   * const payload = this.userService.payload;
   * if (payload) {
   *   console.log('User permissions:', payload.module_permissions);
   *   console.log('Role:', payload.role_name);
   * }
   * ```
   */
  public get payload(): IUserPayload | null {
    return this._userPayloadHandler()
  }

  /**
   * BehaviorSubject for user information model state management
   * @private
   * @readonly
   * @type {BehaviorSubject<UserInfoModel | null>}
   * @description Central state management for user personal information model.
   * Stores Models only, never DTOs.
   */
  private readonly _userInfoModel$: BehaviorSubject<UserInfoModel | null> = new BehaviorSubject<UserInfoModel | null>(null);

  /**
   * Observable stream that emits whenever the user information changes.
   * 
   * Subscribe to this observable to get notified when user data is loaded,
   * updated, or cleared. The observable emits the user information model
   * or null if there's no user logged in.
   * 
   * @returns An observable that emits the current user information model
   */
  public get userInfo$(): Observable<UserInfoModel | null> {
    return this._userInfoModel$.asObservable();
  }

  /**
   * Gets the current user information synchronously.
   * 
   * This property gives you immediate access to the user information without
   * subscribing to an observable. It returns the model if a user is logged in,
   * or null if there's no user data available.
   * 
   * @returns The current user information model or null
   */
  public get userInfo(): UserInfoModel | null {
    return this._userInfoModel$.value;
  }

  /**
   * Checks whether user information is currently available.
   * 
   * This is a convenience method to quickly check if a user is logged in
   * without having to check for null yourself.
   * 
   * @returns True if user information exists, false otherwise
   */
  public hasUserInfo(): boolean {
    return this.userInfo !== null;
  }

  /**
   * Clears all user information from the service.
   * 
   * This method removes the current user data and sets it to null. It's
   * typically called when a user logs out. All components subscribed to
   * the userInfo observable will be notified of the change.
   */
  public clearUserInfo(): void {
    this._userInfoModel$.next(null);
  }

  /**
   * Observable stream of module permissions changes
   * @public
   * @readonly
   * @type {Observable<IUserPayload['module_permissions'] | null>}
   * @description Emits the current module permissions whenever they change.
   * Derived from user payload data and provides granular permission information.
   * 
   * @example
   * ```typescript
   * this.userService.modulePermissions$.subscribe(permissions => {
   *   if (permissions) {
   *     console.log('Dashboard permissions:', permissions[ContentPageModules.DASHBOARD]);
   *     console.log('User management permissions:', permissions[ContentPageModules.USERS]);
   *   }
   * });
   * ```
   */
  public readonly modulePermissions$!: Observable<IUserPayload['module_permissions'] | null>;

  /**
   * Current module permissions for the user
   * @public
   * @readonly
   * @type {IUserPayload['module_permissions'] | null}
   * @description Returns the current module permissions mapping or null if not available.
   * Provides synchronous access to user permission data.
   * 
   * @example
   * ```typescript
   * const permissions = this.userService.modulePermissions;
   * if (permissions) {
   *   const dashboardPerms = permissions[ContentPageModules.DASHBOARD];
   *   if (dashboardPerms?.read) {
   *     // User can read dashboard data
   *   }
   * }
   * ```
   */
  public get modulePermissions(): IUserPayload['module_permissions'] | null {
    return this._userPayloadHandler()?.module_permissions ?? null;
  }

  /**
   * Observable stream of authorized units changes
   * @public
   * @readonly
   * @type {Observable<IUserPayload['authorized_units'] | null>}
   * @description Emits the current authorized units whenever they change.
   * Derived from user payload data and provides unit access information.
   * 
   * @example
   * ```typescript
   * this.userService.authorizedUnits$.subscribe(units => {
   *   if (units) {
   *     console.log('User can access units:', units);
   *     // Filter data based on authorized units
   *     this.filterDataByUnits(units);
   *   }
   * });
   * ```
   */
  public readonly authorizedUnits$!: Observable<IUserPayload['authorized_units'] | null>;

  /**
   * Current authorized units for the user
   * @public
   * @readonly
   * @type {IUserPayload['authorized_units'] | null}
   * @description Returns the current list of authorized unit IDs or null if not available.
   * Provides synchronous access to user unit authorization data.
   * 
   * @example
   * ```typescript
   * const units = this.userService.authorizedUnits;
   * if (units) {
   *   console.log('User can access units:', units);
   *   // Check if user can access specific unit
   *   if (units.includes('unit-123')) {
   *     // User can access this unit
   *   }
   * }
   * ```
   */
  public get authorizedUnits(): IUserPayload['authorized_units'] | null {
    return this._userPayloadHandler()?.authorized_units ?? null;
  }

  /**
   * User's role identifier
   * @public
   * @readonly
   * @type {string}
   * @description Returns the unique ID of the role assigned to the user.
   * Extracted from user payload data for role-based access control.
   * 
   * @example
   * ```typescript
   * const roleId = this.userService.roleId;
   * if (roleId === 'admin') {
   *   // User has admin role
   *   this.enableAdminFeatures();
   * }
   * ```
   */
  public get roleId(): string {
    return this._userPayloadHandler()?.role_id ?? '';
  }

  /**
   * User's role name
   * @public
   * @readonly
   * @type {string}
   * @description Returns the human-readable name of the user's role.
   * Extracted from user payload data for display and authorization purposes.
   * 
   * @example
   * ```typescript
   * const roleName = this.userService.roleName;
   * console.log('User role:', roleName);
   * 
   * if (roleName === PermissionName.ADMIN) {
   *   // User is an administrator
   * }
   * ```
   */
  public get roleName(): string {
    return this._userPayloadHandler()?.role_name ?? '';
  }

  /**
   * User's facility identifier
   * @public
   * @readonly
   * @type {string}
   * @description Returns the unique ID of the facility the user belongs to.
   * Extracted from user payload data for facility-based access control.
   * 
   * @example
   * ```typescript
   * const facilityId = this.userService.facilityId;
   * console.log('User facility:', facilityId);
   * 
   * // Filter data by facility
   * this.loadFacilityData(facilityId);
   * ```
   */
  public get facilityId(): string {
    return this._userPayloadHandler()?.facility_id ?? '';
  }

  /**
   * Authorization utilities instance for permission management
   * @private
   * @readonly
   * @type {AuthorizationUtils}
   * @description Instance of AuthorizationUtils for handling permission checks and access control.
   * Initialized with module permissions observable for reactive permission management.
   */
  public readonly _authorizationUtils!: AuthorizationUtils

  /**
   * Constructor for UserService
   * @constructor
   * @param {IAuthAccessTokenService} _authAccessToken - Authentication access token service for JWT management
   * @description Initializes the user service with JWT token management and authorization utilities.
   * Sets up reactive streams for user data and permissions based on JWT token changes.
   * 
   * @example
   * ```typescript
   * // Service is automatically injected by Angular DI
   * constructor(@Inject(AUTH_ACCESS_TOKEN) private _authAccessToken: IAuthAccessTokenService) {}
   * ```
   */
  constructor(

    @Inject(AUTH_ACCESS_TOKEN)
    private _authAccessToken: IAuthAccessTokenService
  ) {

    const _userPayload$ = this._authAccessToken.accessToken$.pipe(map((accessToken) => {
      try {

        //
        if (accessToken == null) {
          throw new Error('token is null')
        }

        // decode
        const _instance = new JwtUtils<IUserPayload>(accessToken);

        // get payload
        const _payload = _instance.payload();

        return _payload;

      }
      catch (err) {
        return null;
      }
    })
    );

    this.payload$ = _userPayload$

    this.modulePermissions$ = this.payload$.pipe(map(x => x?.module_permissions ?? null));

    this.authorizedUnits$ = this.payload$.pipe(map(x => x?.authorized_units ?? null));

    this._authorizationUtils = new AuthorizationUtils(this.modulePermissions$);

  }

  /**
   * Updates the user information with data from the API.
   * 
   * This method accepts a data transfer object from the API and converts it
   * to a model before storing it. This is the boundary where API data enters
   * the application, so the conversion happens here automatically.
   * 
   * If you pass null, it will clear the user information, which is useful
   * when logging out.
   * 
   * @param value - The user information data transfer object from the API, or null to clear
   */
  public setUserInfo(value: IUserInfoDto | null): void {
    if (value == null) {
      this._userInfoModel$.next(null);
      return;
    }
    
    // Convert DTO to Model using mapper
    const model = UserDtoMapper.toUserInfoModel(value);
    this._userInfoModel$.next(model);
  }

  /**
   * Checks if user has owner role
   * @public
   * @method isOwner
   * @returns {boolean} True if user has owner role, false otherwise
   * @description Determines if the user has the highest privilege level (facility owner).
   * 
   * @example
   * ```typescript
   * if (this.userService.isOwner()) {
   *   // User is facility owner - show all features
   *   this.enableAllFeatures();
   * }
   * ```
   */
  public isOwner(): boolean {
    return this.roleName === PermissionName.OWNER
  }

  /**
   * Gets module permission object for a specific module
   * @public
   * @method getModulePermission
   * @param {ContentPageModules} moduleName - The module to check permissions for
   * @returns {IModulePermission | null} Permission object or null if no access
   * @description Retrieves the complete permission object for the specified module.
   * Delegates to AuthorizationUtils for permission management.
   * 
   * @example
   * ```typescript
   * const permission = this.userService.getModulePermission(ContentPageModules.USERS);
   * if (permission?.read) {
   *   // User can read user data
   * }
   * 
   * if (permission?.create && permission?.update) {
   *   // User can manage user data
   * }
   * ```
   */
  public getModulePermission(moduleName: ContentPageModules): IModulePermission | null {
    return this._authorizationUtils.getModulePermission(moduleName)
  }

  /**
   * Checks if user has any access to a module
   * @public
   * @method hasModulePermission
   * @param {ContentPageModules} moduleName - The module to check access for
   * @returns {boolean} True if user has any access to the module
   * @description Determines if the user has any level of access to the specified module.
   * Delegates to AuthorizationUtils for permission checking.
   * 
   * @example
   * ```typescript
   * if (this.userService.hasModulePermission(ContentPageModules.REPORTS)) {
   *   // User has some level of access to reports
   *   this.showReportsModule();
   * }
   * ```
   */
  public hasModulePermission(moduleName: ContentPageModules): boolean {
    return this._authorizationUtils.hasModulePermission(moduleName)
  }

  /**
   * Checks if user has viewer access to a module
   * @public
   * @method hasViewerAccessToModule
   * @param {ContentPageModules} moduleName - The module to check viewer access for
   * @returns {boolean} True if user has viewer-level access
   * @description Determines if the user can view/read data from the specified module.
   * Delegates to AuthorizationUtils for permission checking.
   * 
   * @example
   * ```typescript
   * if (this.userService.hasViewerAccessToModule(ContentPageModules.DASHBOARD)) {
   *   // User can view dashboard data
   *   this.loadDashboardData();
   * }
   * ```
   */
  public hasViewerAccessToModule(moduleName: ContentPageModules): boolean {
    return this._authorizationUtils.hasViewerAccessToModule(moduleName)
  }

  /**
   * Checks if user has operator access to a module
   * @public
   * @method hasOperatorAccessToModule
   * @param {ContentPageModules} moduleName - The module to check operator access for
   * @returns {boolean} True if user has operator-level access
   * @description Determines if the user can perform operational tasks in the specified module.
   * Delegates to AuthorizationUtils for permission checking.
   * 
   * @example
   * ```typescript
   * if (this.userService.hasOperatorAccessToModule(ContentPageModules.SYSTEM)) {
   *   // User can operate system functions
   *   this.enableSystemControls();
   * }
   * ```
   */
  public hasOperatorAccessToModule(moduleName: ContentPageModules): boolean {
    return this._authorizationUtils.hasOperatorAccessToModule(moduleName)
  }

  /**
   * Checks if user has supervisor access to a module
   * @public
   * @method hasSupervisorAccessToModule
   * @param {ContentPageModules} moduleName - The module to check supervisor access for
   * @returns {boolean} True if user has supervisor-level access
   * @description Determines if the user can manage data in the specified module.
   * Delegates to AuthorizationUtils for permission checking.
   * 
   * @example
   * ```typescript
   * if (this.userService.hasSupervisorAccessToModule(ContentPageModules.USERS)) {
   *   // User can manage user data
   *   this.enableUserManagement();
   * }
   * ```
   */
  public hasSupervisorAccessToModule(moduleName: ContentPageModules): boolean {
    return this._authorizationUtils.hasSupervisorAccessToModule(moduleName)
  }

  /**
   * Checks if user has admin access to a module
   * @public
   * @method hasAdminAccessToModule
   * @param {ContentPageModules} moduleName - The module to check admin access for
   * @returns {boolean} True if user has admin-level access
   * @description Determines if the user has full administrative access to the specified module.
   * Delegates to AuthorizationUtils for permission checking.
   * 
   * @example
   * ```typescript
   * if (this.userService.hasAdminAccessToModule(ContentPageModules.SYSTEM)) {
   *   // User has full admin access to system
   *   this.enableAdminControls();
   * }
   * ```
   */
  public hasAdminAccessToModule(moduleName: ContentPageModules): boolean {
    return this._authorizationUtils.hasAdminAccessToModule(moduleName)
  }

  /**
   * Internal method for handling user payload extraction from JWT token
   * @private
   * @method _userPayloadHandler
   * @returns {IUserPayload | null} User payload data or null if not available
   * @description Extracts user payload data from the current JWT token.
   * Handles token validation and error cases gracefully.
   * 
   * @example
   * ```typescript
   * // Called internally by other methods
   * // No manual invocation required
   * ```
   */
  private _userPayloadHandler(): IUserPayload | null {

    // ES6 Destruction
    const { accessToken } = this._authAccessToken ?? {};

    if (accessToken == null) return null;

    const _instance = new JwtUtils<IUserPayload>(accessToken)

    return _instance.payload();

  }


}
